import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

export default function MantencionesVehiculo({ vehiculo, onClose, usuario }) {
    const [registros, setRegistros] = useState([]);
    const [hitos, setHitos] = useState([]);
    const [personal, setPersonal] = useState([]);
    const [pautaInfo, setPautaInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showCompleteForm, setShowCompleteForm] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);
    const [detailRegistro, setDetailRegistro] = useState(null);

    const [formData, setFormData] = useState({
        id_hito_pauta: '',
        fecha_ingreso: new Date().toISOString().split('T')[0],
        kilometraje_vehiculo: vehiculo.kilometraje_actual || '',
        tipo_ejecucion: 'INTERNA',
        id_mecanico: '',
        taller_externo: '',
        observaciones: ''
    });

    const [completeFormData, setCompleteFormData] = useState({
        fecha_salida: new Date().toISOString().split('T')[0],
        costo_total: '',
        observaciones: '',
        tareas_ejecutadas: []
    });

    // Adjuntos (pdf, word, excel, imágenes)
    const [formFiles, setFormFiles] = useState([]);
    const [adjuntosRegistro, setAdjuntosRegistro] = useState(null);
    const [adjuntos, setAdjuntos] = useState([]);
    const [adjFiles, setAdjFiles] = useState([]);
    const [uploadingAdj, setUploadingAdj] = useState(false);

    useEffect(() => {
        fetchData();
    }, [vehiculo]);

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            
            // 1. Obtener registros del vehículo
            const resRegistros = await fetchWithAuth(`${API_BASE_URL}/api/mantenciones/vehiculo/${vehiculo.id}`);
            if (resRegistros.ok) {
                const regs = await resRegistros.json();
                // Asegurar que tareas_requeridas y tareas_ejecutadas sean arrays
                setRegistros(regs.map(r => ({
                    ...r,
                    tareas_requeridas: Array.isArray(r.tareas_requeridas)
                        ? r.tareas_requeridas
                        : (typeof r.tareas_requeridas === 'string' ? JSON.parse(r.tareas_requeridas) : []),
                    tareas_ejecutadas: Array.isArray(r.tareas_ejecutadas)
                        ? r.tareas_ejecutadas
                        : (typeof r.tareas_ejecutadas === 'string' ? JSON.parse(r.tareas_ejecutadas) : [])
                })));
            }

            // 2. Si tiene pauta, obtener los hitos y la info de la pauta
            if (vehiculo.id_pauta) {
                const [resHitos, resPauta] = await Promise.all([
                    fetchWithAuth(`${API_BASE_URL}/api/mantenciones/pautas/${vehiculo.id_pauta}/hitos`),
                    fetchWithAuth(`${API_BASE_URL}/api/mantenciones/pautas/${vehiculo.id_pauta}`)
                ]);
                if (resHitos.ok) {
                    const hitosData = await resHitos.json();
                    // Asegurar que tareas_requeridas sea siempre un array
                    setHitos(hitosData.map(h => ({
                        ...h,
                        tareas_requeridas: Array.isArray(h.tareas_requeridas)
                            ? h.tareas_requeridas
                            : (typeof h.tareas_requeridas === 'string' ? JSON.parse(h.tareas_requeridas) : [])
                    })));
                }
                if (resPauta.ok) setPautaInfo(await resPauta.json());
            }

            // 3. Obtener personal (para mecánicos)
            // Se asume que el contratista es el mismo del vehículo
            const resPersonal = await fetchWithAuth(`${API_BASE_URL}/api/personal-contratistas/${vehiculo.id_contratista}`);
            if (resPersonal.ok) setPersonal(await resPersonal.json());

        } catch (err) {
            console.error('Error fetching mantenciones data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitRegistro = async (e) => {
        e.preventDefault();
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/mantenciones/registros`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_vehiculo: vehiculo.id,
                    ...formData,
                    kilometraje_vehiculo: parseInt(formData.kilometraje_vehiculo)
                })
            });

            if (response.ok) {
                const creado = await response.json();
                if (formFiles.length > 0 && creado?.id) {
                    await uploadAdjuntos(creado.id, formFiles);
                }
                setShowForm(false);
                setFormFiles([]);
                setFormData({
                    id_hito_pauta: '',
                    fecha_ingreso: new Date().toISOString().split('T')[0],
                    kilometraje_vehiculo: vehiculo.kilometraje_actual || '',
                    tipo_ejecucion: 'INTERNA',
                    id_mecanico: '',
                    taller_externo: '',
                    observaciones: ''
                });
                fetchData();
            } else {
                const err = await response.json();
                alert(err.error || 'Error al guardar registro');
            }
        } catch (error) {
            alert('Error de conexión');
        }
    };

    const handleCompleteRegistro = async (e) => {
        e.preventDefault();
        if (!selectedRegistro) return;

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/mantenciones/registros/${selectedRegistro.id}/completar`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fecha_salida: completeFormData.fecha_salida,
                    costo_total: completeFormData.costo_total ? parseFloat(completeFormData.costo_total) : null,
                    observaciones: completeFormData.observaciones,
                    tareas_ejecutadas: completeFormData.tareas_ejecutadas
                })
            });

            if (response.ok) {
                setShowCompleteForm(false);
                setSelectedRegistro(null);
                setCompleteFormData({ fecha_salida: new Date().toISOString().split('T')[0], costo_total: '', observaciones: '', tareas_ejecutadas: [] });
                fetchData();
            } else {
                alert('Error al completar registro');
            }
        } catch (error) {
            alert('Error de conexión');
        }
    };

    const uploadAdjuntos = async (idRegistro, files) => {
        const data = new FormData();
        files.forEach(f => data.append('adjuntos', f));
        const res = await fetchWithAuth(`${API_BASE_URL}/api/mantenciones/registros/${idRegistro}/adjuntos`, {
            method: 'POST',
            body: data
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            alert(err.error || 'Error al subir adjuntos');
            return false;
        }
        return true;
    };

    const openAdjuntos = async (reg) => {
        setAdjuntosRegistro(reg);
        setAdjFiles([]);
        setShowForm(false);
        setShowCompleteForm(false);
        setShowDetail(false);
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/mantenciones/registros/${reg.id}/adjuntos`);
            if (res.ok) setAdjuntos(await res.json());
        } catch (err) {
            console.error('Error al cargar adjuntos:', err);
        }
    };

    const handleUploadAdjuntos = async () => {
        if (!adjuntosRegistro || adjFiles.length === 0) return;
        setUploadingAdj(true);
        const ok = await uploadAdjuntos(adjuntosRegistro.id, adjFiles);
        if (ok) {
            setAdjFiles([]);
            await openAdjuntos(adjuntosRegistro);
            fetchData();
        }
        setUploadingAdj(false);
    };

    const handleDeleteAdjunto = async (adjId) => {
        if (!window.confirm('¿Eliminar este adjunto?')) return;
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/mantenciones/adjuntos/${adjId}`, { method: 'DELETE' });
            if (res.ok) {
                await openAdjuntos(adjuntosRegistro);
                fetchData();
            } else {
                alert('Error al eliminar el adjunto');
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const iconoAdjunto = (nombre) => {
        const ext = (nombre || '').split('.').pop().toLowerCase();
        if (ext === 'pdf') return '📄';
        if (['doc', 'docx'].includes(ext)) return '📝';
        if (['xls', 'xlsx'].includes(ext)) return '📊';
        if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return '🖼️';
        return '📎';
    };

    return (
        <div className="modal">
            <div className="standard-form" style={{ width: '100%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <div>
                        <h3 style={{ margin: '0 0 5px 0' }}>🛠️ Mantenciones: {vehiculo.patente}</h3>
                        <p style={{ margin: 0, color: 'var(--text-muted)' }}>
                            {vehiculo.marca} {vehiculo.modelo} | KM Actual: {vehiculo.kilometraje_actual?.toLocaleString() || 0}
                            {pautaInfo && (
                                <span style={{ display: 'block', marginTop: '4px', fontWeight: '500', color: '#166534' }}>
                                    📋 Pauta: {pautaInfo.marca} {pautaInfo.modelo} {pautaInfo.descripcion ? `(${pautaInfo.descripcion})` : ''}
                                </span>
                            )}
                            {!pautaInfo && vehiculo.id_pauta && (
                                <span style={{ display: 'block', marginTop: '4px', color: '#854d0e' }}>
                                    ⚠️ Sin pauta de mantención asignada
                                </span>
                            )}
                        </p>
                    </div>
                    <button onClick={onClose} className="btn-secondary" style={{ padding: '8px' }}>✕</button>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    {!showForm && !showCompleteForm && !showDetail && (
                        <button onClick={() => { setShowForm(true); setAdjuntosRegistro(null); }} className="add-button">
                            + Ingresar a Taller
                        </button>
                    )}
                </div>

                {/* Formulario Ingreso */}
                {showForm && (
                    <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                        <h4>Nuevo Ingreso a Taller</h4>
                        <form onSubmit={handleSubmitRegistro}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div className="form-group">
                                    <label>Fecha de Ingreso</label>
                                    <input type="date" required value={formData.fecha_ingreso} onChange={e => setFormData({...formData, fecha_ingreso: e.target.value})} />
                                </div>
                                <div className="form-group">
                                    <label>Kilometraje Actual</label>
                                    <input type="number" required value={formData.kilometraje_vehiculo} onChange={e => setFormData({...formData, kilometraje_vehiculo: e.target.value})} />
                                </div>
                                
                                <div className="form-group">
                                    <label>Motivo / Hito de Pauta</label>
                                    <select value={formData.id_hito_pauta} onChange={e => setFormData({...formData, id_hito_pauta: e.target.value})}>
                                        <option value="">Falla / Mantención Correctiva (Sin pauta)</option>
                                        {hitos.map(h => (
                                            <option key={h.id} value={h.id}>{h.nombre} (A los {h.kilometraje} KM)</option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="form-group">
                                    <label>Tipo de Ejecución</label>
                                    <select value={formData.tipo_ejecucion} onChange={e => setFormData({...formData, tipo_ejecucion: e.target.value})}>
                                        <option value="INTERNA">Taller Interno</option>
                                        <option value="EXTERNA">Taller Externo</option>
                                    </select>
                                </div>

                                {formData.tipo_ejecucion === 'INTERNA' && (
                                    <div className="form-group">
                                        <label>Mecánico Responsable</label>
                                        <select required value={formData.id_mecanico} onChange={e => setFormData({...formData, id_mecanico: e.target.value})}>
                                            <option value="">Seleccione un mecánico...</option>
                                            {personal.map(p => (
                                                <option key={p.id} value={p.id}>{p.nombre} {p.apellido}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {formData.tipo_ejecucion === 'EXTERNA' && (
                                    <div className="form-group">
                                        <label>Nombre del Taller Externo</label>
                                        <input type="text" required value={formData.taller_externo} onChange={e => setFormData({...formData, taller_externo: e.target.value})} placeholder="Ej: Kaufmann" />
                                    </div>
                                )}
                            </div>

                            <div className="form-group" style={{ marginTop: '15px' }}>
                                <label>📎 Documentos adjuntos (PDF, Word, Excel, imágenes)</label>
                                <input
                                    type="file"
                                    multiple
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
                                    onChange={e => setFormFiles(Array.from(e.target.files))}
                                />
                                {formFiles.length > 0 && (
                                    <div style={{ marginTop: '8px', fontSize: '0.85rem', color: 'var(--primary)' }}>
                                        {formFiles.map((f, i) => <div key={i}>✓ {f.name}</div>)}
                                    </div>
                                )}
                            </div>

                            <div className="form-group" style={{ marginTop: '15px' }}>
                                <label>Observaciones de Ingreso</label>
                                <textarea value={formData.observaciones} onChange={e => setFormData({...formData, observaciones: e.target.value})} rows="3"></textarea>
                            </div>

                            <div className="form-actions" style={{ marginTop: '20px' }}>
                                <button type="submit" className="btn-primary">Guardar Ingreso</button>
                                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancelar</button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Detalle de Mantención Completada */}
                {showDetail && detailRegistro && (
                    <div style={{ background: '#f0f9ff', padding: '20px', borderRadius: '8px', border: '1px solid #bae6fd', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h4 style={{ margin: 0 }}>📋 Detalle de Mantención</h4>
                            <button onClick={() => { setShowDetail(false); setDetailRegistro(null); }} className="btn-secondary" style={{ padding: '6px 12px' }}>✕ Cerrar</button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                            <div>
                                <p style={{ margin: '0 0 4px 0', color: 'var(--text-muted)', fontSize: '0.85em' }}>Fecha de Ingreso</p>
                                <p style={{ margin: 0, fontWeight: '500' }}>{new Date(detailRegistro.fecha_ingreso).toLocaleDateString()}</p>
                            </div>
                            <div>
                                <p style={{ margin: '0 0 4px 0', color: 'var(--text-muted)', fontSize: '0.85em' }}>Fecha de Salida</p>
                                <p style={{ margin: 0, fontWeight: '500' }}>{detailRegistro.fecha_salida ? new Date(detailRegistro.fecha_salida).toLocaleDateString() : '-'}</p>
                            </div>
                            <div>
                                <p style={{ margin: '0 0 4px 0', color: 'var(--text-muted)', fontSize: '0.85em' }}>Kilometraje</p>
                                <p style={{ margin: 0, fontWeight: '500' }}>{detailRegistro.kilometraje_vehiculo?.toLocaleString()} KM</p>
                            </div>
                            <div>
                                <p style={{ margin: '0 0 4px 0', color: 'var(--text-muted)', fontSize: '0.85em' }}>Costo Total</p>
                                <p style={{ margin: 0, fontWeight: '500' }}>${detailRegistro.costo_total?.toLocaleString() ?? '-'}</p>
                            </div>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <p style={{ margin: '0 0 4px 0', color: 'var(--text-muted)', fontSize: '0.85em' }}>Motivo / Hito</p>
                            <p style={{ margin: 0, fontWeight: '500' }}>{detailRegistro.nombre_hito || 'Mantención Correctiva'}</p>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <p style={{ margin: '0 0 4px 0', color: 'var(--text-muted)', fontSize: '0.85em' }}>Ejecución</p>
                            <p style={{ margin: 0, fontWeight: '500' }}>
                                {detailRegistro.tipo_ejecucion === 'INTERNA'
                                    ? `Taller Interno - ${detailRegistro.nombre_mecanico} ${detailRegistro.apellido_mecanico}`
                                    : `Taller Externo - ${detailRegistro.taller_externo}`}
                            </p>
                        </div>

                        {detailRegistro.tareas_requeridas && detailRegistro.tareas_requeridas.length > 0 && (
                            <div style={{ marginBottom: '15px', padding: '12px', background: '#fff', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#166534' }}>📋 Tareas de la pauta:</p>
                                {detailRegistro.tareas_requeridas.map((tarea, idx) => {
                                    const ejecutada = detailRegistro.tareas_ejecutadas?.includes(tarea);
                                    return (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '0.95rem' }}>
                                            <span style={{ fontSize: '1.1em' }}>{ejecutada ? '✅' : '⬜'}</span>
                                            <span style={{ textDecoration: ejecutada ? 'none' : 'line-through', color: ejecutada ? '#166534' : '#94a3b8' }}>
                                                {tarea}
                                            </span>
                                            <span style={{ fontSize: '0.8em', color: ejecutada ? '#16a34a' : '#94a3b8', marginLeft: 'auto' }}>
                                                {ejecutada ? 'Realizada' : 'No realizada'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {detailRegistro.observaciones && (
                            <div style={{ marginBottom: '15px' }}>
                                <p style={{ margin: '0 0 4px 0', color: 'var(--text-muted)', fontSize: '0.85em' }}>Observaciones</p>
                                <div style={{ padding: '10px', background: '#fff', borderRadius: '6px', border: '1px solid #e2e8f0', whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>
                                    {detailRegistro.observaciones}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Formulario Completar */}
                {showCompleteForm && selectedRegistro && (
                    <div style={{ background: '#f0fdf4', padding: '20px', borderRadius: '8px', border: '1px solid #bbf7d0', marginBottom: '20px' }}>
                        <h4>Completar Mantención</h4>
                        <p style={{ margin: '0 0 15px 0', color: 'var(--text-muted)' }}>
                            {selectedRegistro.nombre_hito || 'Mantención Correctiva'} - Ingreso: {new Date(selectedRegistro.fecha_ingreso).toLocaleDateString()}
                        </p>
                        <form onSubmit={handleCompleteRegistro}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div className="form-group">
                                    <label>Fecha de Salida</label>
                                    <input type="date" required value={completeFormData.fecha_salida} onChange={e => setCompleteFormData({...completeFormData, fecha_salida: e.target.value})} />
                                </div>
                                <div className="form-group">
                                    <label>Costo Total (Opcional)</label>
                                    <input type="number" step="0.01" value={completeFormData.costo_total} onChange={e => setCompleteFormData({...completeFormData, costo_total: e.target.value})} placeholder="Ej: 150000" />
                                </div>
                            </div>

                            {/* Tareas del hito */}
                            {selectedRegistro.tareas_requeridas && selectedRegistro.tareas_requeridas.length > 0 && (
                                <div className="form-group" style={{ marginTop: '15px' }}>
                                    <label style={{ fontWeight: '600', color: '#166534' }}>📋 Tareas a realizar (marque las ejecutadas):</label>
                                    <div style={{ marginTop: '10px', padding: '12px', background: '#fff', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                                        {selectedRegistro.tareas_requeridas.map((tarea, idx) => (
                                            <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', cursor: 'pointer', fontSize: '0.95rem' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={completeFormData.tareas_ejecutadas.includes(tarea)}
                                                    onChange={e => {
                                                        const checked = e.target.checked;
                                                        setCompleteFormData(prev => ({
                                                            ...prev,
                                                            tareas_ejecutadas: checked
                                                                ? [...prev.tareas_ejecutadas, tarea]
                                                                : prev.tareas_ejecutadas.filter(t => t !== tarea)
                                                        }));
                                                    }}
                                                />
                                                <span>{tarea}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="form-group" style={{ marginTop: '15px' }}>
                                <label>Observaciones de Cierre</label>
                                <textarea value={completeFormData.observaciones} onChange={e => setCompleteFormData({...completeFormData, observaciones: e.target.value})} rows="3" placeholder="Trabajo realizado, repuestos cambiados..."></textarea>
                            </div>
                            <div className="form-actions" style={{ marginTop: '20px' }}>
                                <button type="submit" className="btn-primary" style={{ background: '#16a34a' }}>Marcar como Completada</button>
                                <button type="button" onClick={() => { setShowCompleteForm(false); setSelectedRegistro(null); }} className="btn-secondary">Cancelar</button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Panel de Adjuntos */}
                {adjuntosRegistro && (
                    <div style={{ background: '#fefce8', padding: '20px', borderRadius: '8px', border: '1px solid #fde047', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h4 style={{ margin: 0 }}>📎 Adjuntos — {adjuntosRegistro.nombre_hito || 'Mantención Correctiva'} ({new Date(adjuntosRegistro.fecha_ingreso).toLocaleDateString()})</h4>
                            <button onClick={() => { setAdjuntosRegistro(null); setAdjuntos([]); setAdjFiles([]); }} className="btn-secondary" style={{ padding: '6px 12px' }}>✕ Cerrar</button>
                        </div>

                        {adjuntos.length > 0 ? (
                            <div style={{ marginBottom: '15px' }}>
                                {adjuntos.map(adj => (
                                    <div key={adj.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#fff', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '6px' }}>
                                        <span style={{ fontSize: '1.2em' }}>{iconoAdjunto(adj.nombre_original || adj.nombre_archivo)}</span>
                                        <a
                                            href={`${API_BASE_URL}/uploads/${adj.nombre_archivo}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ flex: 1, color: '#0369a1', textDecoration: 'none', fontWeight: '500', fontSize: '0.9rem' }}
                                        >
                                            {adj.nombre_original || adj.nombre_archivo}
                                        </a>
                                        <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>
                                            {new Date(adj.fecha_creacion).toLocaleDateString()}
                                        </span>
                                        <button
                                            onClick={() => handleDeleteAdjunto(adj.id)}
                                            className="btn-action"
                                            style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', padding: '4px 10px', fontSize: '0.8rem' }}
                                            title="Eliminar adjunto"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No hay documentos adjuntos.</p>
                        )}

                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                                type="file"
                                multiple
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
                                onChange={e => setAdjFiles(Array.from(e.target.files))}
                                style={{ flex: 1, minWidth: '220px' }}
                            />
                            <button
                                onClick={handleUploadAdjuntos}
                                disabled={adjFiles.length === 0 || uploadingAdj}
                                className="btn-primary"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {uploadingAdj ? 'Subiendo...' : '⬆️ Subir adjuntos'}
                            </button>
                        </div>
                        {adjFiles.length > 0 && (
                            <div style={{ marginTop: '8px', fontSize: '0.85rem', color: 'var(--primary)' }}>
                                {adjFiles.map((f, i) => <div key={i}>✓ {f.name}</div>)}
                            </div>
                        )}
                    </div>
                )}

                {/* Historial */}
                {loading ? <p>Cargando historial...</p> : (
                    <div className="table-responsive">
                        <table className="standard-table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>KM</th>
                                    <th>Motivo</th>
                                    <th>Ejecución</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {registros.map(reg => (
                                    <tr key={reg.id}>
                                        <td>
                                            In: {new Date(reg.fecha_ingreso).toLocaleDateString()}<br/>
                                            Out: {reg.fecha_salida ? new Date(reg.fecha_salida).toLocaleDateString() : '-'}
                                        </td>
                                        <td>{reg.kilometraje_vehiculo?.toLocaleString()}</td>
                                        <td>
                                            {reg.nombre_hito || 'Correctiva'}
                                            {reg.adjuntos_count > 0 && (
                                                <div style={{ marginTop: '4px', fontSize: '0.8em', color: '#0369a1' }}>
                                                    📎 {reg.adjuntos_count} adjunto{reg.adjuntos_count !== 1 ? 's' : ''}
                                                </div>
                                            )}
                                            {reg.tareas_ejecutadas && reg.tareas_ejecutadas.length > 0 && (
                                                <div style={{ marginTop: '4px', fontSize: '0.8em', color: '#166534' }}>
                                                    ✅ {reg.tareas_ejecutadas.join(', ')}
                                                </div>
                                            )}
                                            {reg.tareas_requeridas && reg.tareas_requeridas.length > 0 && !reg.tareas_ejecutadas && (
                                                <div style={{ marginTop: '4px', fontSize: '0.8em', color: '#854d0e' }}>
                                                    📋 {reg.tareas_requeridas.length} tareas pendientes
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: reg.tipo_ejecucion === 'INTERNA' ? '#0284c7' : '#ea580c' }}>
                                                {reg.tipo_ejecucion}
                                            </span><br/>
                                            {reg.tipo_ejecucion === 'INTERNA' ? `${reg.nombre_mecanico} ${reg.apellido_mecanico}` : reg.taller_externo}
                                        </td>
                                        <td>
                                            <span style={{
                                                padding: '4px 8px', borderRadius: '12px', fontSize: '0.8em', fontWeight: 'bold',
                                                backgroundColor: reg.estado === 'COMPLETADA' ? '#dcfce7' : '#fef9c3',
                                                color: reg.estado === 'COMPLETADA' ? '#166534' : '#854d0e'
                                            }}>
                                                {reg.estado}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                {reg.estado !== 'COMPLETADA' ? (
                                                    <button 
                                                        onClick={() => {
                                                            setSelectedRegistro(reg);
                                                            setShowCompleteForm(true);
                                                            setShowForm(false);
                                                            setAdjuntosRegistro(null);
                                                        }}
                                                        className="btn-action btn-edit"
                                                        title="Completar"
                                                    >
                                                        ✅ Completar
                                                    </button>
                                                ) : (
                                                    <button 
                                                        onClick={() => {
                                                            setDetailRegistro(reg);
                                                            setShowDetail(true);
                                                            setShowForm(false);
                                                            setShowCompleteForm(false);
                                                            setAdjuntosRegistro(null);
                                                        }}
                                                        className="btn-action"
                                                        style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}
                                                        title="Ver detalle"
                                                    >
                                                        📋 Ver Detalle
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => openAdjuntos(reg)}
                                                    className="btn-action"
                                                    style={{ background: '#fefce8', color: '#854d0e', border: '1px solid #fde047' }}
                                                    title="Ver y adjuntar documentos"
                                                >
                                                    📎 {reg.adjuntos_count > 0 ? reg.adjuntos_count : ''}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {registros.length === 0 && (
                                    <tr><td colSpan="6" style={{textAlign: 'center'}}>No hay mantenciones registradas</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
